<template>
  <div class="cb-page cb-page--fixed agreement-screen">
    <header class="agreement-screen__header">
      <button type="button" class="agreement-screen__back" aria-label="Назад" @click="goBack">
        <AppIcon name="chevron-left" :size="26" />
      </button>
      <h1 class="agreement-screen__title">Юридическое согласие</h1>
    </header>

    <GlassCard class="agreement-screen__card" padding="none" radius="xl">
      <div class="agreement-screen__doc">
        <span class="agreement-screen__doc-icon">
          <AppIcon name="document" :size="32" :stroke-width="1.8" />
        </span>
        <h2 class="agreement-screen__doc-title">Политика конфиденциальности и пользовательское соглашение</h2>
      </div>

      <div class="agreement-text">
        <div
          ref="scroller"
          class="agreement-text__scroller"
          tabindex="0"
          role="region"
          aria-label="Текст соглашения"
          @scroll.passive="scheduleUpdate"
        >
          <div ref="content" class="agreement-text__content">
            <p v-for="(paragraph, index) in INTRO" :key="`intro-${index}`" class="agreement-text__p">{{ paragraph }}</p>

            <section class="agreement-text__section agreement-text__section--camera">
              <h3 class="agreement-text__h">
                <AppIcon name="camera" :size="18" class="agreement-text__h-icon" />
                Камера и обработка видео
              </h3>
              <template v-for="(paragraph, index) in CAMERA_SECTION" :key="`camera-${index}`">
                <p v-if="isBullet(paragraph)" class="agreement-text__li">{{ bulletText(paragraph) }}</p>
                <p v-else class="agreement-text__p">{{ paragraph }}</p>
              </template>
            </section>

            <h3 class="agreement-text__doc-heading">{{ AGREEMENT_TITLE }}</h3>
            <section v-for="section in agreementSections" :key="section.title" class="agreement-text__section">
              <h4 class="agreement-text__h agreement-text__h--small">{{ section.title }}</h4>
              <template v-for="(paragraph, index) in section.paragraphs" :key="`${section.title}-${index}`">
                <p v-if="isBullet(paragraph)" class="agreement-text__li">{{ bulletText(paragraph) }}</p>
                <p v-else class="agreement-text__p">{{ paragraph }}</p>
              </template>
            </section>
          </div>
        </div>

        <div
          v-show="bar.visible"
          ref="track"
          class="agreement-text__track"
          aria-hidden="true"
          @pointerdown.self="onTrackPointer"
        >
          <span
            class="agreement-text__thumb"
            :class="{ 'agreement-text__thumb--drag': dragging }"
            :style="{ height: `${bar.height}px`, transform: `translateY(${bar.top}px)` }"
            @pointerdown.prevent="onThumbDown"
            @pointermove="onThumbMove"
            @pointerup="onThumbUp"
            @pointercancel="onThumbUp"
          ></span>
        </div>
      </div>
    </GlassCard>

    <div class="agreement-screen__actions">
      <AppButton variant="secondary" size="md" class="agreement-screen__btn" :disabled="busy" @click="decline">
        Не согласен
      </AppButton>
      <AppButton variant="primary" size="md" class="agreement-screen__btn" :loading="busy" @click="accept">
        Согласен
      </AppButton>
    </div>
  </div>
</template>

<script setup>
/**
 * AgreementScreen (/agreement) — «Юридическое согласие» (макет 2).
 *
 * Открывается с главного экрана при нажатии «Игра», если соглашение ещё не принято
 * (?next=search). «Согласен» → session.acceptAgreement() → (next=search) запрос камеры → /search,
 * без флага — назад. «Не согласен» → согласие снимается, «/» + тост.
 *
 * Текст: вступление (как на макете) + раздел о камере и видео + разделы из content/agreementText.js.
 * Скроллбар свой (лаймовый ползунок на тёмной дорожке): нативный скрыт, ползунок можно тянуть.
 */
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import GlassCard from "@/components/ui/GlassCard.vue";
import { AGREEMENT_TITLE, agreementSections } from "@/content/agreementText";
import { acquireCamera, cameraErrorMessage, releaseCamera } from "@/services/media";
import telegram from "@/services/telegram";
import { useSessionStore } from "@/stores/session";
import { useRoomStore } from "@/stores/room";
import { useToast } from "@/composables/useToast";

const INTRO = Object.freeze([
  "Настоящая Политика конфиденциальности и Пользовательское соглашение определяют порядок сбора, использования, хранения и защиты вашей персональной информации при использовании игры «Гляделки».",
  "Мы уважаем вашу конфиденциальность и стремимся защищать ваши персональные данные. Мы собираем только ту информацию, которая необходима для обеспечения работы игры, улучшения сервиса и честной игры между участниками.",
  "Ваши данные не передаются третьим лицам без вашего согласия, за исключением случаев, предусмотренных законодательством Российской Федерации.",
  "Используя игру, вы соглашаетесь с условиями настоящего Пользовательского соглашения, включая правила поведения, ограничения ответственности и порядок разрешения споров.",
  "Если вы не согласны с условиями, пожалуйста, не используйте игру. Продолжая, вы подтверждаете, что ознакомились с условиями и принимаете их.",
]);

const CAMERA_SECTION = Object.freeze([
  "Для игры нужен доступ к камере и микрофону вашего устройства. Доступ запрашивается перед началом поиска соперника или входом в комнату и используется только во время игры.",
  "Видео с камеры анализируется непосредственно на вашем устройстве: приложение определяет моргание или закрытие рта в выбранном режиме. Для повтора приложение временно держит короткую последовательность кадров с камеры без звука.",
  "В момент возможного нарушения приложение отправляет на сервер снимок для проверки результата и короткую последовательность кадров без звука для повтора. После подтверждения оба участника видят повтор и ключевой кадр проигрыша.",
  "Видео и звук между соперниками передаются напрямую от устройства к устройству (peer-to-peer). Если прямое соединение невозможно, поток в зашифрованном виде проходит через сервер-ретранслятор и не сохраняется.",
  "Для работы игры также сохраняются:",
  "• игровое имя, аватар и идентификатор Telegram;",
  "• статистика игр (победы, поражения) и баланс звёзд;",
  "• кадры нарушений, относящиеся к завершённым играм.",
]);

const route = useRoute();
const router = useRouter();
const session = useSessionStore();
const toast = useToast();

const busy = ref(false);
const continueToSearch = computed(() => route.query.next === "search");
const continueToRoom = computed(() => route.query.next === "room");
let unmounted = false;

function isBullet(text) {
  return typeof text === "string" && text.startsWith("• ");
}

function bulletText(text) {
  return text.slice(2);
}

/* ------------------------------------------------------------ навигация */

function goHome() {
  const state = window.history.state;
  if (state && state.back === "/") router.back();
  else router.replace("/");
}

function goBack() {
  const state = window.history.state;
  if (state && state.back) router.back();
  else router.replace("/");
}

async function accept() {
  if (busy.value) return;
  session.acceptAgreement();
  telegram.haptic("success");
  if (continueToRoom.value) {
    router.replace({ path: "/room", query: route.query.invite ? { invite: route.query.invite } : {} });
    return;
  }
  if (!continueToSearch.value) {
    goBack();
    return;
  }
  busy.value = true;
  try {
    await acquireCamera();
    // Ушли с экрана, пока ждали разрешения, — камеру не держим.
    if (unmounted) releaseCamera();
    else router.replace("/search");
  } catch (error) {
    if (!unmounted) {
      toast.error(cameraErrorMessage(error));
      goHome();
    }
  } finally {
    busy.value = false;
  }
}

function decline() {
  if (busy.value) return;
  session.revokeAgreement();
  if (continueToRoom.value) useRoomStore().clearIntent();
  toast.info("Без согласия играть нельзя");
  goHome();
}

onBeforeRouteLeave((to) => {
  if (continueToRoom.value && to.name !== "room") useRoomStore().clearIntent();
});

/* ------------------------------------------------------- свой скроллбар */

const scroller = ref(null);
const content = ref(null);
const track = ref(null);
const bar = reactive({ visible: false, top: 0, height: 0 });
const dragging = ref(false);
const MIN_THUMB = 40;

let frame = 0;
let resizeObserver = null;
let drag = null;

function updateBar() {
  frame = 0;
  const el = scroller.value;
  const tr = track.value;
  if (!el || !tr) return;
  const { scrollTop, scrollHeight, clientHeight } = el;
  const scrollable = scrollHeight - clientHeight;
  bar.visible = scrollable > 1;
  if (!bar.visible) return;
  const trackHeight = tr.clientHeight;
  const height = Math.min(trackHeight, Math.max(MIN_THUMB, Math.round((trackHeight * clientHeight) / scrollHeight)));
  bar.height = height;
  bar.top = Math.round((trackHeight - height) * Math.min(1, Math.max(0, scrollTop / scrollable)));
}

function scheduleUpdate() {
  if (!frame) frame = requestAnimationFrame(updateBar);
}

/** Сколько пикселей прокрутки соответствует 1px движения ползунка. */
function scrollPerPixel() {
  const el = scroller.value;
  const tr = track.value;
  if (!el || !tr) return 0;
  const free = tr.clientHeight - bar.height;
  return free > 0 ? (el.scrollHeight - el.clientHeight) / free : 0;
}

function onThumbDown(event) {
  const el = scroller.value;
  if (!el) return;
  drag = { pointerId: event.pointerId, startY: event.clientY, startScroll: el.scrollTop, ratio: scrollPerPixel() };
  dragging.value = true;
  try {
    event.currentTarget.setPointerCapture(event.pointerId);
  } catch (e) {
    // захват указателя недоступен — перетаскивание всё равно работает, пока палец над ползунком
  }
}

function onThumbMove(event) {
  if (!drag || event.pointerId !== drag.pointerId || !scroller.value) return;
  scroller.value.scrollTop = drag.startScroll + (event.clientY - drag.startY) * drag.ratio;
}

function onThumbUp(event) {
  if (!drag || event.pointerId !== drag.pointerId) return;
  drag = null;
  dragging.value = false;
}

/** Тап по дорожке — прокрутка на страницу в сторону тапа. */
function onTrackPointer(event) {
  const el = scroller.value;
  const tr = track.value;
  if (!el || !tr) return;
  const y = event.clientY - tr.getBoundingClientRect().top;
  const direction = y < bar.top ? -1 : 1;
  el.scrollBy({ top: direction * el.clientHeight * 0.85, behavior: "smooth" });
}

onMounted(() => {
  nextTick(updateBar);
  if (typeof ResizeObserver === "function") {
    resizeObserver = new ResizeObserver(scheduleUpdate);
    if (scroller.value) resizeObserver.observe(scroller.value);
    if (content.value) resizeObserver.observe(content.value);
  } else {
    window.addEventListener("resize", scheduleUpdate);
  }
  // Inter подгружается с display=swap — после смены шрифта высота текста меняется.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleUpdate).catch(() => null);
});

onUnmounted(() => {
  unmounted = true;
  if (frame) cancelAnimationFrame(frame);
  if (resizeObserver) resizeObserver.disconnect();
  window.removeEventListener("resize", scheduleUpdate);
});
</script>

<style scoped>
.agreement-screen {
  gap: 14px;
}

/* ---------------------------------------------------------------- header */

.agreement-screen__header {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 60px;
  padding-top: 4px;
}

.agreement-screen__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--cb-border-strong);
  background: linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
  color: var(--cb-text);
  box-shadow: var(--cb-inner-highlight);
  cursor: pointer;
  transition: transform var(--cb-dur-fast) var(--cb-ease), border-color var(--cb-dur) var(--cb-ease);
}

.agreement-screen__back:active {
  transform: scale(0.95);
  border-color: var(--cb-lime-border);
}

.agreement-screen__title {
  min-width: 0;
  font-size: clamp(19px, 5.8vw, 23px);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

/* ------------------------------------------------------------------ card */

.agreement-screen .agreement-screen__card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
}

.agreement-screen__doc {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding: 14px 12px 14px 14px;
  border-radius: var(--cb-radius-md);
  border: 1px solid rgba(221, 245, 90, 0.42);
  background:
    linear-gradient(135deg, rgba(221, 245, 90, 0.13) 0%, rgba(221, 245, 90, 0.04) 100%),
    var(--cb-surface);
  box-shadow: inset 0 0 22px rgba(221, 245, 90, 0.06), 0 0 18px rgba(221, 245, 90, 0.06);
}

.agreement-screen__doc-icon {
  display: inline-flex;
  flex-shrink: 0;
  color: var(--cb-lime);
  filter: drop-shadow(0 0 8px rgba(221, 245, 90, 0.45));
}

.agreement-screen__doc-title {
  min-width: 0;
  font-size: clamp(13.5px, 3.85vw, 16.5px);
  font-weight: 700;
  line-height: 1.3;
  color: var(--cb-text);
}

/* ------------------------------------------------------------ text panel */

.agreement-text {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  border-radius: var(--cb-radius-md);
  border: 1px solid var(--cb-border);
  background: rgba(0, 0, 0, 0.2);
  box-shadow: inset 0 2px 12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.agreement-text__scroller {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  outline: none;
  -webkit-user-select: text;
  user-select: text;
}

.agreement-text__scroller::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.agreement-text__scroller:focus-visible {
  box-shadow: inset 0 0 0 2px var(--cb-lime-border);
  border-radius: inherit;
}

.agreement-text__content {
  padding: 16px 28px 22px 16px;
  color: #BFC4B9;
  font-size: 14.5px;
  line-height: 1.55;
}

.agreement-text__p + .agreement-text__p {
  margin-top: 14px;
}

.agreement-text__section {
  margin-top: 22px;
}

.agreement-text__section--camera {
  padding: 14px 14px 16px;
  border-radius: var(--cb-radius-sm);
  border: 1px solid rgba(221, 245, 90, 0.22);
  background: rgba(221, 245, 90, 0.04);
}

.agreement-text__section--camera .agreement-text__p + .agreement-text__p {
  margin-top: 10px;
}

.agreement-text__h {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--cb-text);
}

.agreement-text__h--small {
  font-size: 15px;
  margin-bottom: 8px;
}

.agreement-text__h-icon {
  color: var(--cb-lime);
}

.agreement-text__doc-heading {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--cb-border);
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
  color: var(--cb-text);
}

.agreement-text__li {
  position: relative;
  margin-top: 6px;
  padding-left: 18px;
}

.agreement-text__li::before {
  content: "";
  position: absolute;
  left: 4px;
  top: 0.66em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--cb-lime);
  box-shadow: 0 0 6px rgba(221, 245, 90, 0.5);
}

/* Дорожка и лаймовый ползунок. */
.agreement-text__track {
  position: absolute;
  top: 14px;
  bottom: 14px;
  right: 8px;
  width: 14px;
  display: flex;
  justify-content: center;
  cursor: pointer;
  touch-action: none;
}

.agreement-text__track::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 4px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  pointer-events: none;
}

.agreement-text__thumb {
  position: absolute;
  top: 0;
  left: 50%;
  width: 5px;
  margin-left: -2.5px;
  border-radius: 999px;
  background: var(--cb-lime);
  box-shadow: 0 0 10px rgba(221, 245, 90, 0.55);
  cursor: grab;
  touch-action: none;
  transition: width var(--cb-dur-fast) var(--cb-ease), margin var(--cb-dur-fast) var(--cb-ease);
}

/* Невидимая зона захвата пошире самого ползунка. */
.agreement-text__thumb::before {
  content: "";
  position: absolute;
  top: -6px;
  bottom: -6px;
  left: -10px;
  right: -10px;
}

.agreement-text__thumb--drag {
  width: 7px;
  margin-left: -3.5px;
  cursor: grabbing;
}

/* --------------------------------------------------------------- actions */

.agreement-screen__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  flex-shrink: 0;
}

.agreement-screen__actions .agreement-screen__btn {
  width: 100%;
  height: 60px;
  padding: 0 12px;
  border-radius: var(--cb-radius-lg);
  font-size: 18px;
  font-weight: 800;
}
</style>
